<ng-container [formGroup]="formGroup">
  @if (regimeDepreciationMethods) {
    <ng-container formArrayName="regimes">
      @for (regime of formArray.controls; track regime; let i = $index) {
        <div
          class="mb-3 form-section"
          [ngClass]="regimeCssClass"
          [formGroupName]="i"
        >
          <div [class]="formArray.controls.length ? 'border rounded p-4' : ''">
            @if (showHeadings) {
              <h4 class="mb-4">
                {{ regime.controls.regimeName.value }}
                {{ !firstUseDate ? 'Default' : '' }} Depreciation
              </h4>
            }

            <app-depreciation-method-modifier
              #modifier
              [regimeId]="regime.controls.regimeId.value"
              [depreciationMethods]="
                depreciationMethodsByRegime[regime.controls.regimeId.value]
              "
              [initialDepreciationMethodId]="
                regimeData?.[i]?.depreciationMethodId
              "
              [acquisitionDate]="acquisitionDate"
              [firstUseDate]="firstUseDate"
              [effectiveDate]="effectiveDate"
              [isFirstUse]="isFirstUse"
            ></app-depreciation-method-modifier>

            <app-form-label>
              Method
              @if (regime.controls.poolCode) {
                / Pool
              }
            </app-form-label>

            <div appValidationContext>
              <select
                authorizeEnable
                [authorizeRegister]="requiresRegisterPermission"
                appFormControl
                class="input-standard"
                aria-label="Depreciation Method"
                [formControl]="
                  methodIdOrPoolCodeByRegime[regime.controls.regimeId.value]
                "
              >
                <option value="">
                  @if (effectiveDate) {
                    Select&hellip;
                  } @else {
                    &ndash;
                  }
                </option>
                @for (
                  option of mergedOptionsByRegime[
                    regime.controls.regimeId.value
                  ] | async;
                  track option.value
                ) {
                  <option
                    [ngValue]="option.value"
                    [class.text-black-50]="
                      option.type === 'pool'
                        ? !isValidPool(
                            option.value,
                            regime.value.depreciationRules
                          )
                        : !isValidMethod(
                            option.value,
                            regime.value.depreciationRules
                          )
                    "
                  >
                    {{ option.name }}
                  </option>
                }
              </select>
              <div *invalidMessageFor="'required'">Method is required</div>
            </div>

            <app-depreciation-rule-selector
              [register]="register"
              [regimeId]="regime.controls.regimeId.value"
              [acquisitionDate]="acquisitionDate"
              [firstUseDate]="firstUseDate"
              formArrayName="depreciationRules"
            >
            </app-depreciation-rule-selector>

            @if (
              regime.controls.depreciationMethodId.value || !allowBlankSettings
            ) {
              <app-depreciation-lookup
                formControlName="lookup"
                [regimeId]="regime.controls.regimeId.value"
                [lifeIsEditable]="isLifeEditable(regime.value)"
                [lifeValues]="getLifeValues(regime.value)"
              ></app-depreciation-lookup>

              @switch (getLifeType(regime.value)) {
                @case ('ReadOnly') {
                  <ng-container *ngTemplateOutlet="ReadOnlyLife"></ng-container>
                }

                @case ('EditLifeOrDate') {
                  <div class="mt-4">
                    <div class="checkbox-enhanced">
                      <input
                        [id]="'useEndDate-' + regime.controls.regimeId.value"
                        type="checkbox"
                        formControlName="useEndDate"
                        authorizeEnable
                        [authorizeRegister]="requiresRegisterPermission"
                      />
                      <label
                        [for]="'useEndDate-' + regime.controls.regimeId.value"
                        class=""
                      >
                        End of Effective Life
                      </label>
                    </div>

                    @if (regime.controls.useEndDate.value) {
                      <app-autocalc-life-end-date
                        class="input-standard d-block"
                        authorizeEnable
                        [authorizeRegister]="requiresRegisterPermission"
                        formControlName="life"
                        appValidationContext
                        [settings]="getDepreciationSettings(regime.value)"
                        [firstUseDate]="firstUseDate"
                        [effectiveDate]="effectiveDate"
                        (remainingLifeChange)="
                          onRemainingLifeChange(regime, $event)
                        "
                      ></app-autocalc-life-end-date>
                    }
                  </div>

                  @if (regime.controls.useEndDate.value) {
                    <ng-container
                      *ngTemplateOutlet="ReadOnlyLife"
                    ></ng-container>
                  } @else {
                    <ng-container *ngTemplateOutlet="LifeInput"></ng-container>
                  }
                }

                @case ('Edit') {
                  <ng-container *ngTemplateOutlet="LifeInput"></ng-container>
                }
              }

              @if (getRemainingLife(regime.value); as remainingLife) {
                <div class="mt-2">
                  <app-form-label>
                    Remaining Life
                    @if (effectiveDate || firstUseDate; as date) {
                      <span class="text-body-secondary">
                        (from
                        <time [attr.datetime]="date" class="text-nowrap">{{
                          date | aadate
                        }}</time
                        >)
                      </span>
                    }
                  </app-form-label>
                  <div
                    class="input-number form-control-plaintext"
                    [class.text-danger]="
                      regime.controls.life.hasError('remainingLife')
                    "
                  >
                    {{ remainingLife | number }} years
                  </div>
                </div>
              }

              <ng-template #ReadOnlyLife>
                <div class="mt-4">
                  <app-form-label>
                    Effective Life
                    @if (firstUseDate; as date) {
                      <span class="text-body-secondary">
                        from
                        <time [attr.datetime]="date" class="text-nowrap">{{
                          date | aadate
                        }}</time>
                      </span>
                    }
                  </app-form-label>

                  <div class="form-control-plaintext">
                    {{ regime.controls.life.value | number }}
                    @if (isUnitsOfUse(regime.value)) {
                      <span> units </span>
                    }
                    @if (!isUnitsOfUse(regime.value)) {
                      <span> years </span>
                    }
                  </div>
                </div>
              </ng-template>

              <ng-template #LifeInput>
                <div class="mt-2">
                  <app-form-label>
                    Effective Life
                    @if (firstUseDate; as date) {
                      <span class="text-body-secondary">
                        (from
                        <time [attr.datetime]="date" class="text-nowrap">{{
                          date | aadate
                        }}</time
                        >)
                      </span>
                    }
                  </app-form-label>

                  <app-autocalc-life
                    class="input-number"
                    authorizeEnable
                    [authorizeRegister]="requiresRegisterPermission"
                    formControlName="life"
                    appValidationContext
                    [settings]="getDepreciationSettings(regime.value)"
                    [firstUseDate]="firstUseDate"
                    [effectiveDate]="effectiveDate"
                    (remainingLifeChange)="
                      onRemainingLifeChange(regime, $event)
                    "
                  ></app-autocalc-life>
                </div>
              </ng-template>

              @if (isRateApplicable(regime.value)) {
                <div class="mt-4">
                  <app-form-label>
                    Rate
                    @if (effectiveDate || firstUseDate; as date) {
                      <span class="text-body-secondary">
                        (applies to the

                        @switch (depreciationBasis(regime.value)) {
                          @case (DepreciationBasis.Cost) {
                            Cost
                          }
                          @default {
                            <abbr title="Written Down Value">WDV</abbr>
                          }
                        }

                        as of
                        <time [attr.datetime]="date" class="text-nowrap">{{
                          date | aadate
                        }}</time
                        >)
                      </span>
                    }
                  </app-form-label>

                  @if (
                    isRateEditable(regime.value) && regime.value.lookup == null
                  ) {
                    <div class="input-number">
                      <app-percentage-input
                        appValidationContext
                        authorizeEnable
                        [authorizeRegister]="requiresRegisterPermission"
                        aria-label="Rate (%)"
                        formControlName="rate"
                      >
                        <div *invalidMessageFor="'required'">
                          Rate is required
                        </div>
                        <div *invalidMessageFor="'min'">
                          Rate must be greater than zero
                        </div>
                      </app-percentage-input>
                    </div>
                  } @else {
                    <div class="form-control-plaintext">
                      {{ (regime.controls.rate.value | aapercent) || '-' }}
                    </div>
                  }
                </div>
              }
            }

            <!--Hide for groups-->
            @if (
              firstUseDate &&
              (isOriginalDateApplicable(regime.value) ||
                isOriginalCostApplicable(regime.value))
            ) {
              <hr />

              <app-alert type="help" size="small">
                If this asset was acquired after its original completion date,
                please provide the Original First Use Date and Original Cost (if
                known).
              </app-alert>

              @if (isOriginalDateApplicable(regime.value)) {
                <div class="input-standard mt-2">
                  <app-form-label>
                    Original First Use Date (if known)
                  </app-form-label>
                  <app-date-input
                    formControlName="originalDate"
                    [maxDate]="effectiveDate"
                  ></app-date-input>
                </div>
              }

              @if (isOriginalCostApplicable(regime.value)) {
                <div class="input-standard mt-2">
                  <app-form-label>
                    Original Construction Cost (if known)
                  </app-form-label>
                  <app-currency-input formControlName="originalCost">
                  </app-currency-input>
                </div>
              }

              <hr />
            }

            @if (
              isResidualValueApplicable(regime.value) && !hideResidualValue
            ) {
              <div class="input-number mt-4">
                <app-form-label> Residual Value </app-form-label>

                <app-currency-input
                  appValidationContext
                  formControlName="residualValue"
                >
                  <div *invalidMessageFor="'sameSign' as error">
                    Residual Value must be
                    {{ error.sameSignIsPositive ? 'positive' : 'negative' }}
                  </div>
                  <div *invalidMessageFor="'absoluteMax' as error">
                    @if (transactions.length) {
                      Residual Value must be smaller than the current
                      <abbr title="Written Down Value">WDV</abbr> of
                      {{ currentWdv | aacurrency }}
                    }
                    @if (!transactions.length && acquisitionPrice) {
                      Residual Value must be smaller than the Acquisition Price
                      of
                      {{ acquisitionPrice | aacurrency }}
                    }
                  </div>
                </app-currency-input>
              </div>
            }

            @if (
              showTaxableUse &&
              regimeHasTaxableUse(regime.value) &&
              formGroup.controls.taxableUse
            ) {
              <div class="input-number mt-4">
                <app-form-control size="small" [columnLayout]="true">
                  <app-form-label [required]="true">Taxable Use</app-form-label>

                  <app-percentage-input
                    appValidationContext
                    aria-label="Taxable Use Percentage"
                    [formControl]="formGroup.controls.taxableUse"
                  >
                    <div *invalidMessageFor="'required'">
                      Taxable Use is required
                    </div>
                    <div *invalidMessageFor="['min', 'max']">
                      Taxable Use must be between 0% and 100%
                    </div>
                  </app-percentage-input>
                </app-form-control>
              </div>
            }

            @if (allowAssessmentData) {
              <div class="mt-4">
                <app-form-control
                  size="full"
                  margin="small"
                  [columnLayout]="true"
                >
                  <app-form-label> Notes </app-form-label>
                  <div>
                    <textarea
                      authorizeEnable
                      [authorizeRegister]="requiresRegisterPermission"
                      id="reassessmentNotes"
                      class="form-control"
                      formControlName="notes"
                      rows="2"
                    ></textarea>
                  </div>
                </app-form-control>

                @for (
                  regimeSetting of regimeSettings[
                    regime.controls.regimeId.value
                  ];
                  track regimeSetting;
                  let settingIndex = $index
                ) {
                  <div class="mt-4" formArrayName="settings">
                    <app-form-label>
                      {{ regimeSetting.title }}
                    </app-form-label>

                    <div
                      class="checkbox-enhanced"
                      [formGroupName]="settingIndex"
                    >
                      <input
                        authorizeEnable
                        [authorizeRegister]="requiresRegisterPermission"
                        [id]="'asset-setting-' + regimeSetting.type"
                        type="checkbox"
                        formControlName="value"
                      />

                      <label [for]="'asset-setting-' + regimeSetting.type">
                        {{ regimeSetting.description }}
                      </label>
                    </div>
                  </div>
                }
              </div>
            }
          </div>
        </div>
      }
    </ng-container>
  } @else {
    <app-loading></app-loading>
  }
</ng-container>
